import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  fetchAutocompleteSuggestions,
  fetchSelectedPlace,
  newSessionToken,
} from "../../lib/google-maps";

const DEBOUNCE_MS = 250;
const LOCATION_BIAS_RADIUS_METRES = 50000;

const FormattableText = ({ formattableText }) => {
  if (!formattableText) return null;
  const { text, matches } = formattableText;
  if (!matches?.length) return <>{text}</>;

  const parts = [];
  let cursor = 0;
  matches.forEach(({ startOffset, endOffset }, index) => {
    parts.push(text.slice(cursor, startOffset));
    parts.push(
      <strong key={index}>{text.slice(startOffset, endOffset)}</strong>,
    );
    cursor = endOffset;
  });
  parts.push(text.slice(cursor));
  return <>{parts}</>;
};

FormattableText.propTypes = {
  formattableText: PropTypes.shape({
    text: PropTypes.string.isRequired,
    matches: PropTypes.arrayOf(
      PropTypes.shape({
        startOffset: PropTypes.number.isRequired,
        endOffset: PropTypes.number.isRequired,
      }),
    ),
  }),
};

const AddressAutocomplete = ({
  id,
  name,
  className,
  value,
  onChange,
  onBlur,
  onSelectPlace,
  placeholder,
  includedPrimaryTypes,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listId = useId();

  const inputElementRef = useRef(null);
  const listRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const locationBiasRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(
    () => () => {
      clearTimeout(debounceRef.current);
    },
    [],
  );

  useEffect(() => {
    const handlePointerDown = (event) => {
      const target = event.target;
      if (inputElementRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        locationBiasRef.current = {
          center: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
          radius: LOCATION_BIAS_RADIUS_METRES,
        };
      },
      (error) => {
        console.error("Error getting user's location:", error);
      },
    );
  }, []);

  const runSearch = useCallback(
    async (input) => {
      const requestId = ++requestIdRef.current;
      sessionTokenRef.current ??= await newSessionToken();
      if (!sessionTokenRef.current) return;
      if (requestId !== requestIdRef.current) return;
      try {
        const predictions = await fetchAutocompleteSuggestions({
          input,
          sessionToken: sessionTokenRef.current,
          includedPrimaryTypes,
          locationBias: locationBiasRef.current,
        });
        if (requestId !== requestIdRef.current) return;
        setSuggestions(predictions);
        setActiveIndex(-1);
        setIsOpen(predictions.length > 0);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error("Address autocomplete failed:", error);
        setSuggestions([]);
        setIsOpen(false);
      }
    },
    [includedPrimaryTypes],
  );

  const handleInputChange = (event) => {
    const input = event.target.value;
    onChange(input);
    clearTimeout(debounceRef.current);
    if (!input.trim()) {
      requestIdRef.current++;
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => void runSearch(input), DEBOUNCE_MS);
  };

  const handleSelect = async (prediction) => {
    setIsOpen(false);
    setSuggestions([]);
    try {
      const place = await fetchSelectedPlace(prediction);
      sessionTokenRef.current = await newSessionToken();
      onChange(place.displayName ?? "");
      onSelectPlace(place);
    } catch (error) {
      console.error("Failed to load the selected place:", error);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (!isOpen || suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        index <= 0 ? suggestions.length - 1 : index - 1,
      );
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      void handleSelect(suggestions[activeIndex]);
    }
  };

  return (
    <>
      <input
        ref={inputElementRef}
        type="text"
        id={id}
        name={name}
        className={className}
        value={value}
        onChange={handleInputChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) setIsOpen(true);
        }}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
        }
        autoComplete="off"
      />
      <div ref={listRef}>
        {isOpen && (
          <ul
            id={listId}
            role="listbox"
            className="absolute inset-x-0 top-full z-20 mt-0.5 max-h-72 overflow-y-auto rounded border border-slate-600 bg-white py-1 text-left shadow-lg"
          >
            {suggestions.map((prediction, index) => (
              <li
                key={prediction.placeId}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void handleSelect(prediction)}
                className={`cursor-pointer px-2 py-1.5 text-xs leading-tight text-gray-700 lg:text-base ${
                  index === activeIndex ? "bg-gray-100" : ""
                }`}
              >
                <FormattableText formattableText={prediction.text} />
                {prediction.secondaryText && (
                  <span className="ml-1 text-gray-500">
                    {prediction.secondaryText.text}
                  </span>
                )}
              </li>
            ))}
            <li className="border-t border-slate-200 px-2 pt-1 text-[0.6rem] text-gray-500 lg:text-xs">
              Google Maps
            </li>
          </ul>
        )}
      </div>
    </>
  );
};

AddressAutocomplete.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onSelectPlace: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  includedPrimaryTypes: PropTypes.arrayOf(PropTypes.string),
};

export default AddressAutocomplete;
